import { useCachedActions } from '@hooks/useCachedActions';
import type { FetchData } from '@hooks/useDataFetcher';
import { buildCacheKey } from '@shared/util/cache-utils';
import { useMemo } from 'react';
import type { IKetoNamespace, IKetoUser } from 'types/custom';
import type { KetoRelationship } from 'types/pop-authz/models/KetoRelationship';

import type { SnackbarActions } from './useSnackbar';

export type User = {
  image?: string;
  username: string;
  userPermissions?: KetoRelationship[];
  info?: { name: string; displayName: string };
};

interface IState {
  loading: boolean;
}

interface IActions {
  getAllUsers: () => Promise<IKetoUser[]>;
  deleteUser: (user: IKetoUser) => Promise<void>;
  getAllNamespaces: () => Promise<IKetoNamespace[]>;
  addUserPermissions: (user: IKetoUser) => Promise<IKetoUser>;
}

export type UsePopAuthz = (
  fetchData: FetchData,
  snackbar: SnackbarActions,
) => [IState, IActions];

export const usePopAuthz: UsePopAuthz = (makeRequest, snackbar) => {
  const { loading, createAction } = useCachedActions(snackbar);

  const actions = useMemo<IActions>(
    () => ({
      getAllUsers: createAction(
        () => buildCacheKey('getAllUsers'),
        async () => {
          const res = await makeRequest<IKetoUser[]>('/api/auth/users');
          return res?.length ? res : [];
        },
        [] as IKetoUser[],
      ),
      getAllNamespaces: createAction(
        () => buildCacheKey('getAllNamespaces'),
        async () => {
          const res = await makeRequest<IKetoNamespace[]>(
            '/api/auth/namespaces',
          );
          return res ?? [];
        },
        [] as IKetoNamespace[],
      ),
      addUserPermissions: createAction(
        (user: IKetoUser) =>
          buildCacheKey('addUserPermissions', {
            cdsid: user.cdsid,
            timestamp: Date.now(),
          }),
        async (user: IKetoUser) =>
          makeRequest<IKetoUser>(
            '/api/auth/users',
            undefined,
            'POST',
            user,
            true,
          ),
        {} as IKetoUser,
      ),
      deleteUser: createAction(
        (user: IKetoUser) =>
          buildCacheKey('deleteUser', {
            cdsid: user.cdsid,
            timestamp: Date.now(),
          }),
        async (user: IKetoUser) => {
          await makeRequest<void>(
            `/api/auth/users/${user.cdsid}`,
            undefined,
            'DELETE',
            undefined,
            true,
          );
        },
        undefined,
      ),
    }),
    [createAction, makeRequest],
  );

  return [{ loading }, actions];
};
